import _defineProperty from "@babel/runtime/helpers/defineProperty";
// Type declarations are used at compile-time as 'headers' only (does NOT include the files)

import { ImageIdentifierInfo } from "abweb/service/image/api/ImageIdentifierInfo";
import { ImagePhysicalScale } from "abweb/service/image/api/ImagePhysicalScale";
import { ImageType } from "abweb/service/image/api/ImageType";
import { ResourceUtils } from "abweb/service/image/utils/ResourceUtils";
import { LogUtils } from "abweb/utils/log/LogUtils";

/**
 * Provide a configurable singleton instance to serve up image identifier information objects about
 * any image used within the system.
 */
export class DefaultImageIdentifierManager {
  constructor() {
    _defineProperty(this, "_dynamicNameSpaceHandlers", {});
    _defineProperty(this, "_brokenImageIdentifier", "ab:broken_image");
  }
  /**
   * This must be invoked before the first call to getInstance or an error will be thrown.
   *
   * @param identifierService
   */
  setImageIdentifierService(identifierService) {
    this._identifierService = identifierService;
  }

  /**
   * Registers a handler for a dynamic namespace (for instance !fa for FontAwesome)
   * @param handler
   */
  registerDynamicNamespaceHandler(handler) {
    this._dynamicNameSpaceHandlers[handler.getNameSpace()] = handler;
  }

  /**
   * Returns the identifier info for the specified identifier or throws an exception if none found
   * @param identifier
   * @returns {ImageIdentifierInfo}
   */
  getIdentifierInfo(identifier) {
    let info = null;
    try {
      info = this.findIdentifierInfo(identifier);
    } catch (err) {
      info = null; // if find identifier throws, we show explosion
    }
    if (info === null) {
      DefaultImageIdentifierManager.LOG.warn("Invalid image identifier specified: " + identifier);
      // TODO: Will Log if not in developer mode. Otherwise, generates an error that will render in the UI
      // ab-dev-err element was getting dropped in the UI for some reason.
      // DebugUtils.developerError("Invalid image identifier specified: " + identifier);
      info = this.getBrokenImageIdentifierInfo(identifier);
    }
    return info;
  }

  /**
   * Returns the path to an image.  Does not work for font images.
   * @param identifier
   * @returns string
   */
  getImagePath(identifier) {
    let info = this.getIdentifierInfo(identifier);
    return info.scales[0].path;
  }
  getBrokenImageIdentifierInfo(theBadIdentifier) {
    const info = this.findIdentifierInfo(this.getBrokenImageIdentifier());
    if (info === null) {
      throw new Error("Invalid identifier specified (" + theBadIdentifier + ") and no broken image to render configured.");
    }
    return info;
  }
  getBrokenImageIdentifier() {
    return this._brokenImageIdentifier;
  }
  setBrokenImageIdentifier(value) {
    this._brokenImageIdentifier = value;
  }
  getImageIdentifierService() {
    return this._identifierService;
  }
  getDynamicIdentifier(identifier) {
    const nameSpaceAndId = ResourceUtils.extractNameSpaceAndId(identifier);
    if (nameSpaceAndId.nameSpace == null) {
      return null;
    }
    if (ResourceUtils.isDynamicNamespace(nameSpaceAndId.nameSpace)) {
      const handler = this._dynamicNameSpaceHandlers[nameSpaceAndId.nameSpace];
      if (handler) {
        return handler.createForIdentifier(nameSpaceAndId);
      } else {
        // TODO: Will Log if not in developer mode. Otherwise, generates an error that will render in the UI
        // ab-dev-err element was getting dropped in the UI for some reason.
        //DebugUtils.developerError("No dynamic namspace handler registered for: " + nameSpaceAndId.nameSpace);

        DefaultImageIdentifierManager.LOG.warn("No dynamic namspace handler registered for: " + nameSpaceAndId.nameSpace);
        return this.getBrokenImageIdentifierInfo(identifier);
      }
    } else if (nameSpaceAndId.nameSpace === "data") {
      // we have to do this because we don't want the dumb cache bust stuff at the end of the path
      let scale = new ImagePhysicalScale("");
      scale.path = identifier;
      return new ImageIdentifierInfo(nameSpaceAndId.nameSpace, nameSpaceAndId.id, ImageType.IMAGE, [scale]);
    }
    return null;
  }

  /**
   * Finds identifier info and returns it, if available. Null otherwise.
   *
   * @param identifier
   * @returns {ImageIdentifierInfo}
   */
  findIdentifierInfo(identifier) {
    let info = this._identifierService.findIdentifierInfo(identifier);
    if (info === null) {
      info = this.getDynamicIdentifier(identifier);
    }
    return info;
  }
}
_defineProperty(DefaultImageIdentifierManager, "LOG", LogUtils.getLogger(__filename));
//# sourceMappingURL=DefaultImageIdentifierManager.js.map
